import { useEffect, useState } from "react";
import { AlertTriangle, Clock } from "lucide-react";
import api from "@/lib/api";
import EarlyStartOverride from "@/components/EarlyStartOverride";

// Round 24 · pay-run review strip. Display-only list of clock patterns
// that fall outside the configured normal-day window. Never affects pay —
// the engine already stored the hours; this is a signal for the operator.
// Hidden for the uploader role (the backend endpoint 403s them via the
// standard allowlist, so this component simply renders nothing on 403).

const KIND_META = {
    early_in:      { label: "In before window", color: "#8A5A00", bg: "#FEF6E6", border: "#C88A17" },
    late_out:      { label: "Out after window", color: "#8A5A00", bg: "#FEF6E6", border: "#C88A17" },
    weekend_punch: { label: "Weekend punch",    color: "#5B2A00", bg: "#FBEAD7", border: "#C06000" },
};

export default function WindowExceptionsStrip({ pid, isUploader, isAdmin, isDraft, onChanged }) {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!pid || isUploader) return;
        let cancelled = false;
        api.get(`/pay-periods/${pid}/window-exceptions`)
            .then((r) => { if (!cancelled) { setData(r.data); setLoaded(true); } })
            .catch(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [pid, isUploader, tick]);

    const onSaved = () => { setTick((t) => t + 1); onChanged?.(); };

    if (isUploader || !loaded) return null;
    const exceptions = data?.exceptions || [];
    if (exceptions.length === 0) return null;

    const normalStart = data?.normal_start || "08:00";
    const normalFinish = data?.normal_finish || "17:00";

    return (
        <section
            data-testid="window-exceptions-strip"
            className="mb-4 border rounded-[3px] bg-white border-neutral-200"
        >
            <header className="flex items-start gap-3 px-4 py-3 border-b border-neutral-200">
                <AlertTriangle size={16} className="mt-0.5" style={{ color: "#C88A17" }} />
                <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold uppercase tracking-[0.14em] text-[11px] text-neutral-700">
                        Window exceptions
                    </div>
                    <p className="text-[12px] text-neutral-600 mt-0.5">
                        {exceptions.length} punch{exceptions.length === 1 ? "" : "es"} outside
                        the normal-day window ({normalStart}–{normalFinish} Mon–Fri).
                        {data?.pay_before_normal_start === false
                            ? ` Early clock-ins are paid from ${normalStart} unless an admin ticks “Authorised early start”.`
                            : " Review signal only."}
                    </p>
                </div>
            </header>
            <ul data-testid="window-exceptions-list" className="divide-y divide-neutral-200">
                {exceptions.map((ex, idx) => {
                    const meta = KIND_META[ex.kind] || { label: ex.kind, color: "#444", bg: "#F3F3F3", border: "#CCC" };
                    return (
                        <li key={`${ex.employee_id || "x"}-${ex.date}-${ex.kind}-${idx}`}>
                        <div
                            data-testid={`window-exceptions-row-${ex.kind}`}
                            className="flex items-center gap-3 px-4 py-2 text-[12.5px]"
                        >
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[2px] text-[10.5px] font-display font-semibold uppercase tracking-[0.1em] border"
                                style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
                            >
                                {meta.label}
                            </span>
                            <span className="font-medium text-neutral-800 truncate">
                                {ex.employee_name || "(unnamed)"}
                            </span>
                            <span className="font-mono-money text-neutral-600">
                                {ex.date}{ex.weekday ? ` · ${ex.weekday}` : ""}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-1 text-neutral-700 font-mono-money">
                                <Clock size={12} />
                                {ex.am_in || "—"} → {ex.pm_out || "—"}
                            </span>
                        </div>
                        {ex.kind === "early_in" && isAdmin && data?.pay_before_normal_start === false && (
                            <EarlyStartOverride pid={pid} ex={ex} canEdit={!!isDraft} onSaved={onSaved} />
                        )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
